import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import { SwipeListComponentProps } from "@components/SwipeList/types";
import { fonts, theme } from "@theme";
import * as rudderstack from "@utils/rudderstack";
import { useCallback } from "react";
import { Keyboard } from "react-native";
import MarqueeText from "@components/MarqueeText";
import StaticArtwork from "@components/BarMusicPlayer/StaticArtwork";
import { router } from "expo-router";
import { getActiveTab } from "@hooks/useActiveTab";
import { MarqueeController } from "@components/MarqueeController";
import LIcon from "@components/LIcon";
import { useTrackIndividualOfflineStatus } from "@utils/downloadManager/hooks";

interface SongDetailsProps extends SwipeListComponentProps {
  itemWidth: number;
  itemHeight: number;
  onPress: () => void;
}

const SongDetails = ({
  item,
  itemWidth,
  itemHeight,
  onPress,
}: SongDetailsProps) => {
  const { artists, title, albums } = item.metadata;
  const { qid } = item;
  // PERFORMANCE: Use qid from props instead of subscribing to activeQueueItemId
  // This prevents re-renders when switching queues
  const artistNamesString =
    [...new Set(artists.map((artist) => artist.name))].join(", ") ?? "";
  const internalOnPress = useCallback(() => {
    Keyboard.dismiss();
    rudderstack.track("press", { value: "open_music_player" });
    onPress();
  }, [onPress]);
  const status = useTrackIndividualOfflineStatus(item.metadata.id);

  const openArtist = useCallback(() => {
    if (artists.length && artists.at(0)) {
      router.navigate({
        pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]`,
        params: {
          artistId: artists[0].id,
          collectionType: "artist",
        },
      });
    }
  }, [artists]);

  return (
    <LBox
      width={itemWidth}
      height={itemHeight}
      flexDirection="row"
      alignItems="center"
    >
      <LBox>
        <StaticArtwork
          url={artists.at(0)?.image ?? albums.at(0)?.image}
          onPress={openArtist}
        />
      </LBox>
      <LBox flexDirection="row" flex={1}>
        <LPressable
          sentryEventName="bar_music_player_song_details_press"
          onPress={internalOnPress}
          style={{ flexDirection: "row", flex: 1, alignItems: "center" }}
        >
          <LBox flex={1} pl="s1" justifyContent="center">
            <MarqueeController resetKey={qid}>
              <MarqueeText
                style={{
                  fontFamily: fonts.medium,
                  color: theme.colors.barPlayerTitle,
                  marginBottom: theme.spacing.s1,
                }}
              >
                {`${title}`}
              </MarqueeText>
              <LBox flexDirection="row" alignItems="center" gap="s1">
                {status === "downloaded" ? (
                  <LIcon name="downloaded" size={12} />
                ) : null}
                <MarqueeText
                  style={{
                    fontFamily: fonts.medium,
                    fontSize: 12,
                    lineHeight: 15,
                    opacity: 0.6,
                    color: theme.colors.barPlayerSubtitle,
                  }}
                >
                  {`${artistNamesString}`}
                </MarqueeText>
              </LBox>
            </MarqueeController>
          </LBox>
        </LPressable>
      </LBox>
    </LBox>
  );
};

export default SongDetails;
